import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { UserPreferencesProvider, useUserPreferences } from './contexts/UserPreferencesContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { ProfileProvider, useProfile } from './contexts/ProfileContext';
import { BugReportProvider } from './contexts/BugReportContext';
import ImpersonationBanner from './components/ImpersonationBanner';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AuthGuard from './components/AuthGuard';
import Protected from './components/Protected';
import PageTransition from './components/PageTransition';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import NoCreditsModal from './components/NoCreditsModal';
import GlobalLowCreditBanner from './components/credits/GlobalLowCreditBanner';
// DESATIVADO TEMPORARIAMENTE: aguardando aprovação do AdSense.
// Para reativar, descomentar este import e o <AdsConsentBanner /> abaixo.
// import AdsConsentBanner from './components/ads/AdsConsentBanner';
import { SubscriptionPaywall } from './components/paywall/SubscriptionPaywall';
import { useInsufficientCreditsHandler } from './hooks/useInsufficientCreditsHandler';
import { useSubscriptionRequiredHandler } from './hooks/useSubscriptionRequiredHandler';
import { needsTermsAcceptance } from './constants/terms';
import { TooltipProvider } from './components/ui';

// Pages — all lazy-loaded to keep the initial bundle lean for the landing route
const Landing = lazy(() => import('./pages/landing/Landing'));
const Partners = lazy(() => import('./pages/partners/Partners'));
const Ajuda = lazy(() => import('./pages/ajuda/Ajuda'));
const AjudaArtigo = lazy(() => import('./pages/ajuda/AjudaArtigo'));
const Login = lazy(() => import('./pages/Login'));
const TermosDeUso = lazy(() => import('./pages/TermosDeUso'));
const PoliticaPrivacidade = lazy(() => import('./pages/PoliticaPrivacidade'));
const Sobre = lazy(() => import('./pages/Sobre'));
const OnboardingPage = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Catalogo = lazy(() => import('./pages/Catalogo'));
const ExemploPublico = lazy(() => import('./pages/ExemploPublico'));
const SimuladoPage = lazy(() => import('./pages/SimuladoPage'));
const Historico = lazy(() => import('./pages/Historico'));
const AttemptDetailPage = lazy(() => import('./pages/AttemptDetailPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const AssinaturaPage = lazy(() => import('./pages/Assinatura'));
const Conta = lazy(() => import('./pages/Conta'));
const Competencias = lazy(() => import('./pages/Competencias'));
const TurmaSimulacoes = lazy(() => import('./pages/TurmaSimulacoes'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminSimulados = lazy(() => import('./pages/admin/AdminSimulados'));
const AdminSimuladoNovo = lazy(() => import('./pages/admin/AdminSimuladoNovo'));
const AdminSimuladoEditar = lazy(() => import('./pages/admin/AdminSimuladoEditar'));
const AdminFeedbacks = lazy(() => import('./pages/admin/AdminFeedbacks'));
const AdminLandingFeedbacks = lazy(() => import('./pages/admin/AdminLandingFeedbacks'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminCredits = lazy(() => import('./pages/admin/AdminCredits'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminPlans = lazy(() => import('./pages/admin/AdminPlans'));
const AdminPlanDetalhe = lazy(() => import('./pages/admin/AdminPlanDetalhe'));
const AdminTopups = lazy(() => import('./pages/admin/AdminTopups'));
const AdminTopupDetalhe = lazy(() => import('./pages/admin/AdminTopupDetalhe'));
const AdminBugs = lazy(() => import('./pages/admin/AdminBugs'));
const AdminBugDetalhe = lazy(() => import('./pages/admin/AdminBugDetalhe'));
const ProfessorTurmas = lazy(() => import('./pages/professor/ProfessorTurmas'));
const ProfessorTurmaDetalhe = lazy(() => import('./pages/professor/ProfessorTurmaDetalhe'));
const ProfessorResultados = lazy(() => import('./pages/professor/ProfessorResultados'));
const ProfessorSimuladoNovo = lazy(() => import('./pages/professor/ProfessorSimuladoNovo'));
const ProfessorTurmaAnalytics = lazy(() => import('./pages/professor/ProfessorTurmaAnalytics'));
const TurmaEntrar = lazy(() => import('./pages/professor/TurmaEntrar'));
const EmpresarialDashboard = lazy(() => import('./pages/empresarial/EmpresarialDashboard'));
const NovaCorrecao = lazy(() => import('./pages/empresarial/NovaCorrecao'));
const HistoricoCorrecoes = lazy(() => import('./pages/empresarial/HistoricoCorrecoes'));
const CorrecaoDetalhe = lazy(() => import('./pages/empresarial/CorrecaoDetalhe'));
const RecursoView = lazy(() => import('./pages/empresarial/RecursoView'));

const PUBLIC_PATHS: readonly string[] = [
  '/',
  '/parceiros',
  '/login',
  '/termos',
  '/privacidade',
  '/sobre',
  '/ajuda',
];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function AppContent() {
  const { preferences } = useUserPreferences();
  const {
    data: profile,
    loading: profileLoading,
    reload: reloadProfile,
    accountType,
  } = useProfile();
  const location = useLocation();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { state: insufficientCreditsState, closeInsufficientCreditsModal } =
    useInsufficientCreditsHandler();
  const { state: paywallState, closeSubscriptionPaywall } = useSubscriptionRequiredHandler();

  useEffect(() => {
    if (profileLoading) {
      setShowTermsModal(false);
      return;
    }
    setShowTermsModal(needsTermsAcceptance(profile));
  }, [profile, profileLoading]);

  const handleTermsAccepted = useCallback(() => {
    setShowTermsModal(false);
    reloadProfile();
  }, [reloadProfile]);

  // Superfícies de crédito são exclusivas do B2B: só o professor/empresarial
  // ainda passa por `creditMiddleware`, então só ele pode receber um 402 ou
  // ficar com saldo baixo. Para o aluno B2C ambas nunca disparam.
  const isBusiness = accountType === 'business';
  const showLowCreditBanner = isBusiness && !isPublicPath(location.pathname);

  return (
    <>
      <TermsAcceptanceModal
        isOpen={showTermsModal}
        onAccepted={handleTermsAccepted}
        // Quem já aceitou uma versão anterior está reaceitando (auditoria do
        // consentimento distingue os dois casos).
        context={profile?.termsAcceptedAt ? 'reacceptance' : 'post-login-modal'}
      />
      <NoCreditsModal
        open={isBusiness && insufficientCreditsState.open}
        onClose={closeInsufficientCreditsModal}
        actionType={insufficientCreditsState.actionType}
      />
      <SubscriptionPaywall
        open={paywallState.open}
        onClose={closeSubscriptionPaywall}
        accessMode={paywallState.accessMode}
        origin={paywallState.origin}
      />
      <Toaster position="top-center" richColors theme={preferences.theme} />
      <div
        style={{
          margin: 0,
          padding: 0,
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <ImpersonationBanner />
        <Navbar />
        {showLowCreditBanner && <GlobalLowCreditBanner currentPath={location.pathname} />}
        {/*
          Bottom sheet de consentimento publicitário. Fica aqui, e não dentro
          das rotas, porque é `position: fixed` — não participa do fluxo, logo
          não interage com os offsets `calc(100vh - Npx)` das telas de altura
          travada. Ele decide sozinho se aparece (perfil carregado, aluno sem
          assinatura, sem decisão vigente).
        */}
        {/* DESATIVADO TEMPORARIAMENTE: aguardando aprovação do AdSense — reativar junto com o import no topo.
        <AdsConsentBanner termsModalOpen={showTermsModal} currentPath={location.pathname} />
        */}
        <AnimatePresence>
          <PageTransition key={location.pathname}>
            <Routes location={location}>
              <Route
                path="/"
                element={
                  <Suspense fallback={null}>
                    <Landing />
                  </Suspense>
                }
              />
              <Route
                path="/parceiros"
                element={
                  <Suspense fallback={null}>
                    <Partners />
                  </Suspense>
                }
              />
              <Route
                path="/ajuda"
                element={
                  <Suspense fallback={null}>
                    <Ajuda />
                  </Suspense>
                }
              />
              <Route
                path="/ajuda/:slug"
                element={
                  <Suspense fallback={null}>
                    <AjudaArtigo />
                  </Suspense>
                }
              />
              <Route
                path="/login"
                element={
                  <Suspense fallback={null}>
                    <Login />
                  </Suspense>
                }
              />
              <Route
                path="/termos"
                element={
                  <Suspense fallback={null}>
                    <TermosDeUso />
                  </Suspense>
                }
              />
              <Route
                path="/privacidade"
                element={
                  <Suspense fallback={null}>
                    <PoliticaPrivacidade />
                  </Suspense>
                }
              />
              <Route
                path="/sobre"
                element={
                  <Suspense fallback={null}>
                    <Sobre />
                  </Suspense>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <AuthGuard>
                    <Suspense fallback={null}>
                      <OnboardingPage />
                    </Suspense>
                  </AuthGuard>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <Dashboard />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/catalogo"
                element={
                  // Vitrine pública: visitante anônimo navega o catálogo sem
                  // login. O gate de auth acontece ao abrir um simulado.
                  <Suspense fallback={null}>
                    <Catalogo />
                  </Suspense>
                }
              />
              <Route
                path="/exemplo/:banca"
                element={
                  // Exemplo de correção público (vitrine): correção sintética
                  // read-only; o chat com IA pede login (ver ExemploPublico).
                  <Suspense fallback={null}>
                    <ExemploPublico />
                  </Suspense>
                }
              />
              <Route
                path="/simulados/:id"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <SimuladoPage />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/historico"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <Historico />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/historico/:attemptId"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <AttemptDetailPage />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/feedback"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <FeedbackPage />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/assinatura"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <AssinaturaPage />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/conta"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <Conta />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/competencias"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <Competencias />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/turmas/:turmaId/simulacoes"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <TurmaSimulacoes />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/admin"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <AdminLayout />
                    </Suspense>
                  </Protected>
                }
              >
                <Route
                  index
                  element={
                    <Suspense fallback={null}>
                      <AdminDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="simulados"
                  element={
                    <Suspense fallback={null}>
                      <AdminSimulados />
                    </Suspense>
                  }
                />
                <Route
                  path="simulados/novo"
                  element={
                    <Suspense fallback={null}>
                      <AdminSimuladoNovo />
                    </Suspense>
                  }
                />
                <Route
                  path="simulados/:id"
                  element={
                    <Suspense fallback={null}>
                      <AdminSimuladoEditar />
                    </Suspense>
                  }
                />
                <Route
                  path="feedbacks"
                  element={
                    <Suspense fallback={null}>
                      <AdminFeedbacks />
                    </Suspense>
                  }
                />
                <Route
                  path="landing-feedbacks"
                  element={
                    <Suspense fallback={null}>
                      <AdminLandingFeedbacks />
                    </Suspense>
                  }
                />
                <Route
                  path="credits"
                  element={
                    <Suspense fallback={null}>
                      <AdminCredits />
                    </Suspense>
                  }
                />
                <Route
                  path="cupons"
                  element={
                    <Suspense fallback={null}>
                      <AdminCoupons />
                    </Suspense>
                  }
                />
                <Route
                  path="planos"
                  element={
                    <Suspense fallback={null}>
                      <AdminPlans />
                    </Suspense>
                  }
                />
                <Route
                  path="planos/:id"
                  element={
                    <Suspense fallback={null}>
                      <AdminPlanDetalhe />
                    </Suspense>
                  }
                />
                <Route
                  path="produtos"
                  element={
                    <Suspense fallback={null}>
                      <AdminTopups />
                    </Suspense>
                  }
                />
                <Route
                  path="produtos/:id"
                  element={
                    <Suspense fallback={null}>
                      <AdminTopupDetalhe />
                    </Suspense>
                  }
                />
                <Route
                  path="bugs"
                  element={
                    <Suspense fallback={null}>
                      <AdminBugs />
                    </Suspense>
                  }
                />
                <Route
                  path="bugs/:id"
                  element={
                    <Suspense fallback={null}>
                      <AdminBugDetalhe />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="/professor/turmas"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <ProfessorTurmas />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/professor/turmas/:turmaId"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <ProfessorTurmaDetalhe />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/professor/turmas/:turmaId/resultados"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <ProfessorResultados />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/professor/simulados/novo"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <ProfessorSimuladoNovo />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/professor/turmas/:turmaId/analytics"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <ProfessorTurmaAnalytics />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/professor/correcoes"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <HistoricoCorrecoes />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/professor/correcoes/nova"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <NovaCorrecao />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/professor/correcoes/:id"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <CorrecaoDetalhe />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/professor/correcoes/:id/recurso"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <RecursoView />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/turma/entrar"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <TurmaEntrar />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/empresarial"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <EmpresarialDashboard />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/empresarial/correcoes"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <HistoricoCorrecoes />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/empresarial/correcoes/nova"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <NovaCorrecao />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/empresarial/correcoes/:id"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <CorrecaoDetalhe />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/empresarial/correcoes/:id/recurso"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <RecursoView />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/correcoes"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <HistoricoCorrecoes />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/correcoes/nova"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <NovaCorrecao />
                    </Suspense>
                  </Protected>
                }
              />
              <Route
                path="/correcoes/:id"
                element={
                  <Protected>
                    <Suspense fallback={null}>
                      <CorrecaoDetalhe />
                    </Suspense>
                  </Protected>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </PageTransition>
        </AnimatePresence>
        {location.pathname !== '/' &&
          location.pathname !== '/parceiros' &&
          !/^\/(simulados|historico\/)/.test(location.pathname) && <Footer />}
      </div>
    </>
  );
}

function App() {
  return (
    <TooltipProvider delayDuration={300}>
      <AuthProvider>
        <ImpersonationProvider>
          <UserPreferencesProvider>
            <ProfileProvider>
              <BugReportProvider>
                <AppContent />
              </BugReportProvider>
            </ProfileProvider>
          </UserPreferencesProvider>
        </ImpersonationProvider>
      </AuthProvider>
    </TooltipProvider>
  );
}

export default App;
